import { useEffect, useContext, useRef, memo, useCallback } from "react";
import GlobalContext from "GlobalContext";
import Overlayer from "components/Overlayer";
import Loader from "components/Loader";

// Qlik internal class names — pinned here so a client upgrade is a 1-line fix
const CELL_SELECTOR = ".qv-st-data-cell";
const VALUE_SELECTOR = ".qv-st-value";

export default memo(function QlikObject({
  id,
  height = "250px",
  showLoader = true,
  showDataView = false,
  noInteraction = false,
  noSelections = false,
  disableInteractivity = false,
  onTapEvent = null,
  maximized = false,
  copyCells = false,
  copiedSuccessMessage = "Скопійовано",
  copiedFailedMessage = "Не вдалося скопіювати",
}) {
  const { app, qlik } = useContext(GlobalContext);
  const el = useRef(null);
  const qViz = useRef(null);
  const toastTimer = useRef(null);
  const toastEl = useRef(null);

  const showToast = useCallback((x, y, text) => {
    // remove any toast still on screen
    clearTimeout(toastTimer.current);
    if (toastEl.current) {
      toastEl.current.remove();
      toastEl.current = null;
    }

    const toast = document.createElement("div");
    toast.className = "qcopy-toast";
    toast.textContent = text;
    toast.style.left = `${x}px`;
    toast.style.top = `${y}px`;
    document.body.appendChild(toast);
    toastEl.current = toast;

    // force reflow so the transition runs
    // eslint-disable-next-line no-unused-expressions
    toast.offsetHeight;
    toast.classList.add("qcopy-toast--show");

    toastTimer.current = setTimeout(() => {
      toast.classList.remove("qcopy-toast--show");
      setTimeout(() => {
        toast.remove();
        if (toastEl.current === toast) toastEl.current = null;
      }, 200);
    }, 900);
  }, []);

  useEffect(() => {

    if (id === "CurrentSelections") {
      app.getObject(el.current, id).then((qmodel) => {
        qViz.current = qmodel;
      });
    } else {
      app.visualization.get(id).then((qmodel) => {
        qmodel.show(el.current, { noInteraction, noSelections });
        qViz.current = qmodel;
        // console.log('qmodel = ', qmodel);
      });
    }

    return () => {
      // console.log("Unmount ", id); //, el, qViz.current);
      // mc.destroy();
      if (qViz.current) {
        qViz.current.close();
        qViz.current = null;
      }
    };
  }, [app, id, noInteraction, noSelections, el]);

  useEffect(() => {
    if (qViz.current) qlik.resize(id);
  }, [qlik, id, height, maximized]);

  useEffect(() => {
    if (qViz.current) qViz.current.toggleDataView();
  }, [showDataView]);

// Right-click a table cell to copy its value
  useEffect(() => {
    if (!copyCells) return undefined;
    const node = el.current;
    if (!node) return undefined;
    const wrapper = node.parentNode;          // <-- defined here

    const handler = async (e) => {
      let target = document.elementFromPoint(e.clientX, e.clientY);

      const overlay = wrapper.querySelector(".chart-overlayer");
      if (overlay && (target === overlay || overlay.contains(target))) {
        const prevPE = overlay.style.pointerEvents;
        overlay.style.pointerEvents = "none";
        target = document.elementFromPoint(e.clientX, e.clientY);
        overlay.style.pointerEvents = prevPE;
      }

      const cell = target && target.closest(".qv-st-data-cell");
      if (!cell || !node.contains(cell)) return;

      const valueEl = cell.querySelector(".qv-st-value") || cell;
      const text = valueEl.textContent.trim();
      if (!text) return;

      e.preventDefault();
      e.stopPropagation();

      try {
        await navigator.clipboard.writeText(text);
        showToast(e.clientX, e.clientY, copiedSuccessMessage);
      } catch (err) {
        console.error("Copy failed", err);
        showToast(e.clientX, e.clientY, copiedFailedMessage);
      }
    };

    wrapper.addEventListener("contextmenu", handler, true);

    return () => {                            // <-- cleanup INSIDE the same effect
      wrapper.removeEventListener("contextmenu", handler, true);
      clearTimeout(toastTimer.current);
      if (toastEl.current) {
        toastEl.current.remove();
        toastEl.current = null;
      }
    };
  }, [copyCells, showToast, copiedSuccessMessage, copiedFailedMessage]);

  // className={showDataView ? 'qdataview' : 'qchart'}
  return (
    <>
      {disableInteractivity && <Overlayer height={height} onTapEvent={onTapEvent} /> }
      <div
        id={id}
        ref={el}
        className={`qchart ${maximized ? "maximized" : ""} ${copyCells ? "qcopy-enabled" : ""}`}
        style={maximized ? {} : { height }}
      >
        {showLoader && <Loader />}
      </div>
    </>
  );
});
